import { useCallback, useContext, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { DashboardContext } from '../contexts/DashboardContext';
import { DashboardsContext } from '../contexts/DashboardsContext';
import { addWorkspaceToDashboardFunc, getDashboardQueriesFunc } from '../lib/helper';
import { Dashboard, DashboardQueriesType, DashboardQuery } from '../types/files';
import ArrayUtils from '../utils/ArrayUtils';
import { filterInRecord, getFromRecord, removeFromRecord, setInRecord } from '../utils/recordUtils';
import useBackwardsCompatible from './useBackwardsCompatible';

export const ExcludeExportQueries = [
  'Digest',
  'Competitive Advantage',
  'Growth',
  'Email Summaries',
  'LinkedIn Posts',
  'Note Summary',
  'Questions',
  'Recent News',
  'Screenshot Summaries',
  'Similar Companies',
  'Tags',
  'Differentiating Factors',
  'Similar Companies 2.0',
  'Investment Thesis',
];

const useBulkDashboards = () => {
  const { userGroup } = useContext(AuthContext);
  const { dashboards, dashboardsCollection, setDashboards, setDashboardsCollection } = useContext(DashboardsContext);
  const { defaultQueryTemplateValues } = useContext(DashboardContext);
  const { dashboardWorkspaces } = useBackwardsCompatible();
  const [loading, setLoading] = useState<boolean>(false);

  const getBulkDashboards = useCallback((collection: string) =>
    getFromRecord(dashboardsCollection, collection) ?? []
  , [dashboardsCollection]);

  const clearBulk = useCallback((collection?: string) => {
    if (collection)
      setDashboardsCollection(prev => setInRecord(prev, collection, []));
    else
      setDashboardsCollection({});
  }, [setDashboardsCollection]);

  const addToBulk = useCallback((collection: string, dashboard: Dashboard, queries?: DashboardQuery[]) => {
    setDashboardsCollection(prev => {
      const collections = getFromRecord(prev, collection) ?? [];

      if (!collections.find(item => item.dashboard.id === dashboard.id))
        return setInRecord(prev, collection, [...collections, { dashboard, queries: queries ?? [] }]);

      return prev;
    });
  }, [setDashboardsCollection]);

  const removeFromBulk = useCallback((collection: string, dashboard: Dashboard) => {
    setDashboardsCollection(prev => {
      const collections = filterInRecord(prev, collection, item => item.dashboard.id !== dashboard.id);
      const remaining = collections[collection];

      return !!remaining?.length ? collections : removeFromRecord(collections, collection);
    });
  }, [setDashboardsCollection]);

  const buildExportable = useCallback((collection: string, selectedTitles?: string[]) => {
    return new Promise<DashboardQueriesType[]>((resolve, reject) => {
      const bulkDashboards = getBulkDashboards(collection);
      const promises = bulkDashboards.map(item => getDashboardQueriesFunc(item.dashboard.id));

      setLoading(true);
      Promise.all(promises)
        .then((queryGroups: DashboardQuery[][]) => {
          const flatQueries = queryGroups.flat();
          const exports = bulkDashboards.map(exportable => ({...exportable,
            queries: flatQueries.filter(query =>
              query.dashboardId === exportable.dashboard.id
              && !ExcludeExportQueries.includes(query.title)
              && (selectedTitles ?? [query.title]).includes(query.title)
            ).map(query => ({...query,
                title: (defaultQueryTemplateValues
                  .find(defQuery => defQuery?.title === query.title)?.displayTitle || query.title)
                    .replace('2.0', ''),
                order: defaultQueryTemplateValues.find(defQuery => defQuery.title === query.title)?.order || query.order
              })).sort((qA, qB) => qA.order - qB.order),
          }));

          resolve(exports);
        })
        .catch(reject)
        .finally(() => setLoading(false));
    });
  }, [defaultQueryTemplateValues, getBulkDashboards, setLoading]);

  const assignToWorkspaces = useCallback((collection: string, workspaceIds: string[]) => {
    return new Promise((resolve, reject) => {
      const bulkDashboards = getBulkDashboards(collection);
      const promises: Promise<Dashboard>[] = [];

      setLoading(true);
      bulkDashboards.forEach(({ dashboard }) => {
        const existingIds = dashboardWorkspaces(dashboard) ?? [];
        const toAssign = workspaceIds.filter(id => !existingIds.includes(id));

        toAssign.forEach(id => {
          promises.push( addWorkspaceToDashboardFunc({
            dashboardId: dashboard.id,
            workspaceId: id,
            group: userGroup,
          }));
        });
      });

      Promise.all(promises)
        .then((dashboardUpdates: Dashboard[]) => {
          const updatedList = dashboards.map(dashboard => {
            const matching = dashboardUpdates.filter(dU => dU.id === dashboard.id);

            return matching.length ? ArrayUtils.sortByDescending(matching, 'createdAt')[0] : dashboard;
          });

          setDashboards(ArrayUtils.sortByDescending(updatedList, 'createdAt'));
          resolve(true);
        })
        .catch(reject)
        .finally(() => setLoading(false));
    });
  }, [getBulkDashboards, setLoading, dashboardWorkspaces, userGroup, dashboards, setDashboards] );

  return { loading, getBulkDashboards, clearBulk, addToBulk, removeFromBulk, buildExportable, assignToWorkspaces };
};

export default useBulkDashboards;